import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { MapPin, Briefcase, IndianRupee, Search, ArrowLeft, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobsListing = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterLocation, setFilterLocation] = useState('');

  useEffect(() => { fetchJobs(); }, []);

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API}/jobs`);
      setJobs(res.data);
    } catch { /* silently fail */ }
    finally { setLoading(false); }
  };

  const filteredJobs = jobs.filter(job => {
    const matchSearch = !search || job.title.toLowerCase().includes(search.toLowerCase()) || job.location.toLowerCase().includes(search.toLowerCase()) || job.description.toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === 'all' || job.job_type === filterType;
    const matchLocation = !filterLocation || job.location.toLowerCase().includes(filterLocation.toLowerCase());
    return matchSearch && matchType && matchLocation;
  });

  const locations = [...new Set(jobs.map(j => j.location))];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
          <div className="flex items-center space-x-3 mb-4">
            <Button data-testid="back-to-home" variant="ghost" size="sm" onClick={() => navigate('/')} className="text-white hover:bg-white/10 -ml-2"><ArrowLeft className="w-4 h-4 mr-1" />Home</Button>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Career</h1>
          <p className="text-slate-300 text-base md:text-lg">Join PrimeSwift — find career opportunities near you</p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-4 -mt-6">
        <Card className="border-slate-100 shadow-lg">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                <Input data-testid="search-input" placeholder="Search jobs..." className="pl-10 h-12 bg-slate-50" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="filter-type" className="h-12 bg-slate-50"><SelectValue placeholder="Job Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="full-time">Full-time</SelectItem>
                  <SelectItem value="part-time">Part-time</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterLocation || 'all'} onValueChange={(v) => setFilterLocation(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-location" className="h-12 bg-slate-50"><SelectValue placeholder="Location" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.map(loc => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Jobs Grid */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            <span className="ml-3 text-slate-600">Loading jobs...</span>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-20">
            <Briefcase className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No jobs available</h3>
            <p className="text-slate-500">Check back later for new opportunities</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-4">{filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''} found</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredJobs.map(job => (
                <Card key={job.id} data-testid={`job-card-${job.id}`} className="border-slate-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer group" onClick={() => navigate(`/driver-jobs/${job.id}`)}>
                  <CardContent className="pt-6 space-y-4">
                    <div className="flex items-start justify-between">
                      <h3 className="text-lg font-bold text-slate-900 group-hover:text-orange-600 transition-colors">{job.title}</h3>
                      <Badge className={job.job_type === 'full-time' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>{job.job_type === 'full-time' ? 'Full-time' : 'Part-time'}</Badge>
                    </div>
                    <div className="space-y-2 text-sm text-slate-600">
                      <div className="flex items-center space-x-2"><MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" /><span>{job.location}</span></div>
                      <div className="flex items-center space-x-2"><IndianRupee className="w-4 h-4 text-slate-400 flex-shrink-0" /><span>{job.salary_range}</span></div>
                    </div>
                    <p className="text-sm text-slate-500 line-clamp-2">{job.description}</p>
                    <Button data-testid={`view-job-${job.id}`} className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full h-10 font-semibold">View Details</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default JobsListing;
